import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, Clock, Send, Loader2 } from "lucide-react";
import { CONTACTS } from "../data/content";
import { Reveal } from "../components/Reveal";
import { Footer } from "../components/Footer";
import { Seo } from "../components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition-all duration-200 focus:border-sky-500 focus:ring-4 focus:ring-sky-100";

const INFO = [
  { icon: Phone, label: "Telefonas", value: CONTACTS.phone, href: `tel:${CONTACTS.phone.replace(/\s/g, "")}` },
  { icon: Mail, label: "El. paštas", value: CONTACTS.email, href: `mailto:${CONTACTS.email}` },
  { icon: Clock, label: "Darbo laikas", value: CONTACTS.hours, href: null },
];

export default function Contacts() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.message) {
      toast.error("Užpildykite vardą, telefoną ir žinutę.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Žinutė išsiųsta! Atsakysime artimiausiu metu.");
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch {
      toast.error("Nepavyko išsiųsti. Pabandykite dar kartą arba skambinkite mums.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="contacts-page">
      <Seo
        title="Kontaktai — ART Ceiling"
        description="Susisiekite dėl įtempiamų lubų: +370 677 73413, mbartceiling@gmail.com. Nemokamas matavimas, dirbame visoje Lietuvoje."
      />
      <section className="grain relative overflow-hidden bg-navy-950 pb-16 pt-32 lg:pb-24 lg:pt-44">
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-sky-300">Kontaktai</p>
            <h1 className="mt-4 max-w-2xl font-heading text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
              Pakalbėkime apie jūsų erdvę
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg">
              Skambinkite, rašykite arba palikite žinutę — atsakome per 15 minučių darbo valandomis.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-16 sm:py-24">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:gap-20 lg:px-12">
          <Reveal>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
              {INFO.map(({ icon: Icon, label, value, href }) => (
                <div
                  key={label}
                  data-testid={`contact-info-${label.toLowerCase().replace(/\s/g, "-")}`}
                  className="rounded-2xl border border-slate-200 bg-slate-50 p-6 transition-all duration-300 hover:border-navy-900/20 hover:bg-white hover:shadow-lg"
                >
                  <Icon className="h-6 w-6 text-sky-600" />
                  <div className="mt-4 text-xs font-bold uppercase tracking-wider text-slate-500">{label}</div>
                  {href ? (
                    <a href={href} className="mt-1.5 block font-heading text-base font-semibold text-navy-900 hover:text-sky-600">
                      {value}
                    </a>
                  ) : (
                    <div className="mt-1.5 font-heading text-base font-semibold text-navy-900">{value}</div>
                  )}
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="rounded-3xl border border-slate-200 bg-slate-50 p-6 sm:p-10">
              <h2 className="font-heading text-xl font-bold text-navy-900">Parašykite mums</h2>
              <form onSubmit={submit} data-testid="contact-form" className="mt-6 grid grid-cols-1 gap-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <input
                    data-testid="contact-name-input"
                    className={inputCls}
                    placeholder="Vardas *"
                    value={form.name}
                    onChange={set("name")}
                  />
                  <input
                    data-testid="contact-phone-input"
                    className={inputCls}
                    placeholder="Telefonas *"
                    value={form.phone}
                    onChange={set("phone")}
                  />
                </div>
                <input
                  data-testid="contact-email-input"
                  type="email"
                  className={inputCls}
                  placeholder="El. paštas (neprivaloma)"
                  value={form.email}
                  onChange={set("email")}
                />
                <textarea
                  data-testid="contact-message-input"
                  className={`${inputCls} min-h-[140px] resize-none`}
                  placeholder="Jūsų žinutė *"
                  value={form.message}
                  onChange={set("message")}
                />
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-submit-button"
                  className="group inline-flex items-center justify-center gap-2 rounded-full bg-navy-900 px-8 py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-navy-800 disabled:opacity-60"
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  Siųsti žinutę
                </button>
              </form>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </main>
  );
}
